import { useEffect } from "react";

/**
 * Attaches an IntersectionObserver that flips `.is-revealed` on any element
 * with `[data-reveal]` inside the current view. One-shot per element.
 *
 * Fully respects prefers-reduced-motion (CSS handles the no-op).
 *
 * Usage: call `useReveal()` once inside a page component. Then decorate any
 * element with `data-reveal` (optionally `data-reveal-delay="120"` for ms).
 */
export default function useReveal(options = {}) {
    const { threshold = 0.12, rootMargin = "0px 0px -8% 0px" } = options;

    useEffect(() => {
        // Skip observation for reduced-motion users — the CSS already
        // renders these elements as visible.
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            document.querySelectorAll("[data-reveal], [data-reveal-up]").forEach((el) => el.classList.add("is-revealed"));
            return;
        }
        // iter87 — observe BOTH legacy [data-reveal] and new [data-reveal-up].
        const els = document.querySelectorAll("[data-reveal]:not(.is-revealed), [data-reveal-up]:not(.is-revealed)");
        if (!els.length) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const el = entry.target;
                        const delay = parseInt(el.getAttribute("data-reveal-delay") || "0", 10);
                        setTimeout(() => el.classList.add("is-revealed"), delay);
                        io.unobserve(el);
                    }
                });
            },
            { threshold, rootMargin }
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [threshold, rootMargin]);
}
